import { ChatSurveyNextAction } from "@pulse/pulse-rpcs/lib/survey-respondent/chatSurveyNextAction";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  getStringDefaultIfUndefined,
  isBrowserLightMode,
  PRIMARY_FONT,
} from "@pulse/shared-components";
import { observer } from "mobx-react";
import {
  useFoundationColorTokens,
  useSpacing,
  useTypography,
  Snackbar,
} from "@surya-digital/leo-reactjs-material-ui";
import React, { useEffect, useState } from "react";
import {
  processSurveyLiveParams,
  useRouteNavigator,
} from "../../../routes/RoutesHelper";
import { useSurveyStore } from "../../root/store/hooks";
import {
  ChatSurveyErrors,
  SaveAndGetNextQuestionErrors,
} from "../../root/store/SurveyStore";
import { SubmitSurveyDialog } from "../../surveyRespondent/components/SubmitSurveyDialog";
import { ChatSurveySection } from "./ChatSurveySection";
import { ChatSurveyTimeline } from "./ChatSurveyTimeline";

interface ChatSurveyExperienceProps {
  onLanguageChangeClick: () => void;
  onStartSurvey: () => void;
}

export const ChatSurveyExperience = observer(
  ({
    onLanguageChangeClick,
    onStartSurvey,
  }: ChatSurveyExperienceProps): React.ReactElement | null => {
    const surveyStore = useSurveyStore();
    const navigateTo = useRouteNavigator();
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const typography = useTypography();
    const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
    const [isSubmitConfirmationDismissed, setIsSubmitConfirmationDismissed] =
      useState(false);
    const [isAnswerEditing, setIsAnswerEditing] = useState(false);
    const surveySettings = surveyStore.surveySettings;
    const submitConfirmation =
      surveyStore.chatSurveyStore.nextAction?.case ===
      "SUBMIT_CONFIRMATION_REQUIRED"
        ? (surveyStore.chatSurveyStore
            .nextAction as ChatSurveyNextAction.SubmitConfirmationRequiredDTO)
        : null;
    const isCompleteWithoutThankYou =
      surveyStore.chatSurveyStore.nextAction?.case === "SURVEY_COMPLETED" &&
      !surveyStore.chatSurveyStore.elements.some(
        (element) => element.case === "THANK_YOU",
      );
    const shouldShowError =
      surveyStore.rpcError === SaveAndGetNextQuestionErrors.InvalidAnswerJson ||
      surveyStore.rpcError === ChatSurveyErrors.SubmissionFailed;

    useEffect(() => {
      if (
        surveyStore.rpcError !== ChatSurveyErrors.QuestionNotAvailable &&
        surveyStore.rpcError !== ChatSurveyErrors.SubmissionNotReady &&
        surveyStore.rpcError !== ChatSurveyErrors.ChatInterfaceNotEnabled
      ) {
        return;
      }
      surveyStore.clearRpcErrors();
      processSurveyLiveParams(async (surveyLinkId, isPreviewURL) => {
        await surveyStore.getCurrentQuestionAndSettings(
          surveyLinkId,
          isPreviewURL,
        );
      }, navigateTo);
    }, [navigateTo, surveyStore, surveyStore.rpcError]);

    useEffect(() => {
      if (submitConfirmation && !isSubmitConfirmationDismissed) {
        setIsSubmitDialogOpen(true);
      } else if (!submitConfirmation) {
        setIsSubmitDialogOpen(false);
        setIsSubmitConfirmationDismissed(false);
      }
    }, [isSubmitConfirmationDismissed, submitConfirmation]);

    if (!surveySettings || isCompleteWithoutThankYou) {
      return null;
    }

    const themedImageLight = surveySettings.themedImage?.lightImageURL;
    const themedImageDark = surveySettings.themedImage?.darkImageURL;

    return (
      <>
        <Snackbar
          message={surveySettings.genericErrorMessageDescriptionText}
          severity="error"
          onClose={surveyStore.clearRpcErrors}
          isOpen={shouldShowError}
          isDismissible={true}
        />
        {submitConfirmation && (
          <SubmitSurveyDialog
            isSubmitSurveyDialogOpen={isSubmitDialogOpen}
            setIsSubmitSurveyDialogOpen={(): void => {
              setIsSubmitDialogOpen(false);
            }}
            primaryButtonText={
              submitConfirmation.submitConfirmationDialogProperties
                .primaryActionText.text
            }
            secondaryButtonText={
              submitConfirmation.submitConfirmationDialogProperties
                .secondaryActionText?.text
            }
            onPrimaryButtonClick={(): void => {
              setIsSubmitConfirmationDismissed(true);
              processSurveyLiveParams(async (surveyLinkId) => {
                await surveyStore.submitChatSurvey(surveyLinkId);
              }, navigateTo);
            }}
            onSecondaryButtonClick={(): void => {
              setIsSubmitConfirmationDismissed(true);
            }}
            title={
              submitConfirmation.submitConfirmationDialogProperties.titleText
                .text
            }
            description={
              submitConfirmation.submitConfirmationDialogProperties
                .descriptionText.text
            }
            spacing={spacing}
            tokens={tokens}
            typography={typography}
          />
        )}
        <ChatSurveySection
          logoUrl={
            isBrowserLightMode()
              ? themedImageLight
              : themedImageDark
                ? themedImageDark
                : themedImageLight
          }
          language={getStringDefaultIfUndefined(
            surveySettings.selectedLanguageCode,
          )}
          isProgressAllowed={surveySettings.isProgressPercentageShown ?? false}
          progressPercentage={surveyStore.progressPercentage}
          onLanguageChangeClick={onLanguageChangeClick}
          areButtonsDisabled={surveyStore.isNetworkCallMade}
          isSingleLanguageAvailable={surveyStore.isSingleLanguageAvailable}
          isLanguageSelectionPersistent={
            surveySettings.isLanguageSelectionOptionPersistent ?? false
          }
          headerText={surveyStore.headerText}
          isHeaderHidden={isAnswerEditing}
          isOuterScrollLocked={isAnswerEditing}
        >
          <ChatSurveyTimeline
            elements={surveyStore.chatSurveyStore.elements}
            timelineRevision={surveyStore.chatSurveyStore.timelineRevision}
            nextAction={surveyStore.chatSurveyStore.nextAction}
            getElementKey={surveyStore.chatSurveyStore.getElementKey}
            startButtonText={surveySettings.startButtonText}
            nextButtonText={surveySettings.nextButtonText}
            previousButtonText={surveySettings.backButtonText}
            groupNextQuestionButtonText={
              surveySettings.miscellaneousSurveySettingsText
                .chatGroupNextQuestionButtonText
            }
            groupSendResponseButtonText={
              surveySettings.miscellaneousSurveySettingsText
                .chatGroupSendResponseButtonText
            }
            areActionsDisabled={
              surveyStore.isNetworkCallMade || surveyStore.isRPCLoading
            }
            isEditingLayoutActive={isAnswerEditing}
            fontFamily={surveySettings.font ?? PRIMARY_FONT}
            fontScale={
              surveySettings.fontSizeScale ??
              SurveySettingsEnums.FontSizeScale.FontSizeScale.DEFAULT_SCALE
            }
            attachmentConfig={
              surveyStore.rteFileServiceStore.richTextAttachmentProps
            }
            dropdownPlaceholder={
              surveySettings.miscellaneousSurveySettingsText
                .singleSelectDropdownChooseOptionText
            }
            multiSelectMaxSelectionText={
              surveySettings.miscellaneousSurveySettingsText
                .multiSelectAndRankingMaxSelectionText
            }
            rankingAllOptionsMandatoryText={
              surveySettings.miscellaneousSurveySettingsText
                .areAllRankingOptionsMandatoryText
            }
            resetAnswerButtonText={surveySettings.resetAnswerButtonText}
            editAnswerButtonText={
              surveySettings.miscellaneousSurveySettingsText
                .editAnswerButtonText
            }
            editAnswersButtonText={
              surveySettings.miscellaneousSurveySettingsText
                .editAnswersButtonText
            }
            cancelEditAnswerButtonText={
              surveySettings.miscellaneousSurveySettingsText
                .cancelEditAnswerButtonText
            }
            onEditStateChange={setIsAnswerEditing}
            submitRetryPrompt={
              submitConfirmation &&
              isSubmitConfirmationDismissed &&
              !isAnswerEditing
                ? {
                    description:
                      submitConfirmation.submitConfirmationDialogProperties
                        .descriptionText.text,
                    buttonText:
                      submitConfirmation.submitConfirmationDialogProperties
                        .primaryActionText.text,
                    disabled:
                      surveyStore.isNetworkCallMade || surveyStore.isRPCLoading,
                    onClick: (): void => {
                      setIsSubmitConfirmationDismissed(false);
                      setIsSubmitDialogOpen(true);
                    },
                  }
                : undefined
            }
            openEndedShowMoreText={
              surveySettings.miscellaneousSurveySettingsText
                .chatOpenEndedShowMoreText
            }
            openEndedShowLessText={
              surveySettings.miscellaneousSurveySettingsText
                .chatOpenEndedShowLessText
            }
            skippedAnswerText={
              surveySettings.miscellaneousSurveySettingsText
                .chatSkippedAnswerText
            }
            onStartSurvey={onStartSurvey}
            onSaveAnswer={(questionId, answerJson): void => {
              processSurveyLiveParams(async (surveyLinkId) => {
                await surveyStore.saveChatSurveyAnswer(
                  surveyLinkId,
                  questionId,
                  answerJson,
                );
              }, navigateTo);
            }}
          />
        </ChatSurveySection>
      </>
    );
  },
);
